<template>
  <div>
    <!-- Botón Agregar Producto -->
    <v-btn 
      v-if="showAddButton && !readonly && !containerExists" 
      color="primary" 
      @click="openAddProductDialog" 
      :dense="dense" 
      class="mb-4"
    >
      Agregar Producto
    </v-btn>

    <!-- Tabla de Productos -->
    <v-text-field v-model="search" append-icon="search" :label="$t('Buscar')" single-line hide-details></v-text-field>

    <v-data-table 
      :headers="headers" 
      :items="localProducts" 
      :items-per-page="10" 
      hide-default-footer
      disable-pagination
      :loading="loading"
      loading-text="Cargando productos..."
      :search="search"
    >
      <template v-slot:item="{ item, index }">
        <tr>
          <td>{{ index + 1 }}</td>
          <td>
            <strong>{{ item.name }}</strong>
          </td>
          <td class="text-center">
            {{ item.quantity }}
          </td>
          <td v-if="!containerExists && !readonly" class="text-center" style="width: 100px">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small color="blue" class="mr-2" @click="editProduct(item, index)">
                  edit
                </v-icon>
              </template>
              <span>Editar</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small color="red" @click="removeProduct(index)">
                  delete
                </v-icon>
              </template>
              <span>Eliminar</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Modal Agregar/Editar Producto -->
    <v-dialog v-model="productDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ editingProduct ? 'Editar Producto' : 'Agregar Producto' }}
        </v-card-title>

        <v-card-text>
          <v-combobox 
            v-model="selectedProduct" 
            :items="availableProducts" 
            item-text="name" 
            item-value="id"
            label="Producto" 
            return-object 
            class="mt-4"
          ></v-combobox>

          <v-text-field 
            v-model.number="productQuantity" 
            type="number" 
            min="1" 
            label="Cantidad"
          ></v-text-field>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="closeProductDialog">Cancelar</v-btn>
          <v-btn 
            color="success" 
            @click="saveProductLocal" 
            text 
            :disabled="!selectedProduct || !productQuantity || productQuantity <= 0"
          >
            {{ editingProduct ? 'Editar' : 'Agregar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'ProductManager',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    availableProducts: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showAddButton: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    containerExists: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      productDialog: false,
      selectedProduct: null,
      productQuantity: 1,
      editingProduct: false,
      editingIndex: null,
      search: "",
    };
  },
  computed: {
    localProducts() {
      return this.value || [];
    },
    headers() {
      const baseHeaders = [
        { text: "#", value: "number", width: "80px", sortable: false },
        { text: "Producto", value: "name", align: "left", sortable: true },
        { text: "Cantidad", value: "quantity", align: "center", sortable: true, width: "150px" },
      ];
      
      // Solo mostrar columna de acciones si el contenedor no existe
      if (!this.containerExists && !this.readonly) {
        baseHeaders.push({ text: "Acciones", value: "actions", align: "center", sortable: false, width: "150px" });
      }
      
      return baseHeaders;
    },
  },
  methods: {
    openAddProductDialog() {
      this.editingProduct = false;
      this.editingIndex = null;
      this.selectedProduct = null;
      this.productQuantity = 1;
      this.productDialog = true;
    },
    editProduct(product, index) {
      this.editingProduct = true;
      this.editingIndex = index;

      // Buscar el producto completo en availableProducts
      const fullProduct = this.availableProducts.find(p => p.id === product.id);

      this.selectedProduct = fullProduct || {
        id: product.id,
        name: product.name,
      };
      this.productQuantity = product.quantity;
      this.productDialog = true;
    },
    closeProductDialog() {
      this.productDialog = false;
      this.selectedProduct = null;
      this.productQuantity = 1;
      this.editingProduct = false;
      this.editingIndex = null;
    },
    saveProductLocal() {
      // Validaciones
      if (!this.selectedProduct) {
        this.$toast.error("Debe seleccionar un producto");
        return;
      }

      if (!this.productQuantity || this.productQuantity <= 0) {
        this.$toast.error("La cantidad debe ser mayor a 0");
        return;
      }

      // Obtener el nombre del producto de forma consistente
      
      const newProduct = {
        id: this.selectedProduct.id,
        name: this.selectedProduct.name,
        quantity: this.productQuantity,
      };

      let updatedProducts = [...this.localProducts];

      if (this.editingProduct) {
        // Editar producto existente
        updatedProducts[this.editingIndex] = newProduct;
        this.$emit('input', updatedProducts);
        this.$emit('product-edited', { product: newProduct, index: this.editingIndex });
      } else {
        // Verificar duplicados
        const exists = updatedProducts.find(p => p.id === this.selectedProduct.id);
        if (exists) {
          this.$toast.warning("Este producto ya está en la lista");
          return;
        }

        // Agregar nuevo producto
        updatedProducts.push(newProduct);
        this.$emit('input', updatedProducts);
        this.$emit('product-added', newProduct);
      }

      this.closeProductDialog();
    },
    removeProduct(index) {
      const updatedProducts = [...this.localProducts];
      const removedProduct = updatedProducts[index];
      updatedProducts.splice(index, 1);
      
      this.$emit('input', updatedProducts);
      this.$emit('product-removed', { product: removedProduct, index });
    },
  },
};
</script>

<style scoped>
.headline {
  font-weight: 500;
}
</style>
